class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.transition?.cancel();
    this.galleryResize?.disconnect();
    this.events = new AbortController();
    const signal = this.events.signal;
    this.panels = [...this.querySelectorAll('[data-media-id]')];
    this.thumbs = [...this.querySelectorAll('[data-media-target]')];
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.activeId = null;
    this.gesture = null;
    this.suppressZoomUntil = 0;
    this.motion.addEventListener('change', () => this.transition?.cancel(), {signal});
    this.addEventListener('click', (event) => {
      const step = event.target.closest('[data-gallery-step]');
      if (step) this.stepMedia(Number(step.dataset.galleryStep));
      const primary = event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey;
      const thumb = event.target.closest('[data-media-target]');
      if (thumb && primary) {
        event.preventDefault(); this.showMedia(thumb.dataset.mediaTarget);
      }
      const zoom = event.target.closest('[data-zoom]');
      if (zoom && primary) {
        event.preventDefault();
        if (performance.now() < this.suppressZoomUntil) return;
        const dialog = this.querySelector('dialog');
        const photo = document.createElement('img');
        photo.src = zoom.href;
        photo.alt = zoom.querySelector('img')?.alt || '';
        photo.decoding = 'async';
        dialog.querySelector('[data-zoom-content]').replaceChildren(photo);
        dialog.showModal();
      }
      const modelButton = event.target.closest('[data-load-model]');
      if (modelButton && window.Shopify?.loadFeatures) {
        modelButton.disabled = true;
        const box = modelButton.parentElement;
        const template = box.querySelector('template');
        Shopify.loadFeatures([{name: 'model-viewer-ui', version: '1.0', onLoad: (error) => {
          if (error || !this.isConnected) { modelButton.disabled = false; return; }
          box.append(template.content.cloneNode(true));
          modelButton.remove();
          if (!document.getElementById('ModelViewerStyles')) {
            const style = document.createElement('link');
            style.id = 'ModelViewerStyles';
            style.rel = 'stylesheet';
            style.href = 'https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css';
            document.head.append(style);
          }
          new Shopify.ModelViewerUI(box.querySelector('model-viewer'));
        }}]);
      }
    }, {signal});
    this.addEventListener('keydown', (event) => {
      if (!event.target.closest('[data-media-target], [data-gallery-step]') || event.altKey || event.ctrlKey || event.metaKey) return;
      if (!this.panels.length || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home' || event.key === 'End') this.showMedia(this.panels[event.key === 'Home' ? 0 : this.panels.length - 1].dataset.mediaId);
      else this.stepMedia(event.key === 'ArrowRight' ? 1 : -1);
      if (event.target.closest('[data-media-target]')) this.querySelector('[data-media-target][aria-current]')?.focus({preventScroll: true});
    }, {signal});
    const stage = this.querySelector('.gallery-stage');
    const updateNavigation = () => {
      this.positionNavigation();
      this.centerThumbnail(false);
    };
    stage?.addEventListener('load', (event) => {
      if (event.target.matches('.gallery-panel:not([hidden]) .gallery-zoom>img')) updateNavigation();
    }, {signal, capture: true});
    if (stage) {
      this.galleryResize = new ResizeObserver(updateNavigation);
      this.galleryResize.observe(stage);
    }
    stage?.addEventListener('pointerdown', (event) => {
      this.gesture = null;
      this.suppressZoomUntil = 0;
      if (event.pointerType === 'mouse' || !event.isPrimary || !event.target.closest('[data-zoom]')) return;
      this.gesture = {id: event.pointerId, x: event.clientX, y: event.clientY, started: performance.now()};
    }, {signal, passive: true});
    stage?.addEventListener('pointermove', (event) => {
      if (!this.gesture || event.pointerId !== this.gesture.id) return;
      const dx = Math.abs(event.clientX - this.gesture.x), dy = Math.abs(event.clientY - this.gesture.y);
      if (dy > 12 && dy > dx) this.gesture = null;
    }, {signal, passive: true});
    stage?.addEventListener('pointerup', (event) => {
      const gesture = this.gesture; this.gesture = null;
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
      if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.3 || performance.now() - gesture.started > 1500) return;
      this.suppressZoomUntil = performance.now() + 500;
      this.stepMedia(dx < 0 ? 1 : -1);
    }, {signal, passive: true});
    stage?.addEventListener('pointercancel', () => { this.gesture = null; }, {signal});
    const controls = this.querySelector('[data-gallery-controls]');
    if (controls) controls.hidden = false;
    const target = location.hash.match(/Media-.+-(\d+)$/)?.[1];
    const initial = this.panels.find(panel => !panel.hidden)?.dataset.mediaId;
    this.showMedia(this.panels.some(panel => panel.dataset.mediaId === target) ? target : initial, {animate: false, announce: false});
  }
  disconnectedCallback() { this.events?.abort(); this.transition?.cancel(); this.galleryResize?.disconnect(); this.gesture = null; }
  positionNavigation() {
    const stage = this.querySelector('.gallery-stage');
    if (!stage) return;
    const photo = stage.querySelector('.gallery-panel:not([hidden]) .gallery-zoom>img');
    let insetX = 0, insetY = 0;
    if (photo?.naturalWidth && photo.naturalHeight) {
      const scale = Math.min(photo.clientWidth / photo.naturalWidth, photo.clientHeight / photo.naturalHeight);
      insetX = Math.max(0, (photo.clientWidth - photo.naturalWidth * scale) / 2);
      insetY = Math.max(0, (photo.clientHeight - photo.naturalHeight * scale) / 2);
    }
    stage.style.setProperty('--gallery-image-inset-x', `${insetX}px`);
    stage.style.setProperty('--gallery-image-inset-y', `${insetY}px`);
  }
  centerThumbnail(animate) {
    const strip = this.querySelector('.gallery-thumbs');
    const active = this.querySelector('[data-media-target][aria-current]');
    if (!strip || !active) return;
    const bounds = strip.getBoundingClientRect(), thumbBounds = active.getBoundingClientRect();
    strip.scrollTo({left: strip.scrollLeft + thumbBounds.left - bounds.left - (bounds.width - thumbBounds.width) / 2, behavior: animate && !this.motion.matches ? 'smooth' : 'auto'});
  }
  stepMedia(direction) {
    if (this.panels.length < 2) return;
    const current = this.panels.findIndex(panel => panel.dataset.mediaId === this.activeId);
    const index = Math.max(0, Math.min(this.panels.length - 1, current + direction));
    this.showMedia(this.panels[index].dataset.mediaId);
  }
  showMedia(id, {animate = true, announce = true} = {}) {
    const next = this.panels.find(panel => panel.dataset.mediaId === String(id));
    if (!next || this.activeId === String(id)) return;
    const previous = this.panels.findIndex(panel => panel.dataset.mediaId === this.activeId);
    const index = this.panels.indexOf(next);
    this.transition?.cancel();
    this.panels.forEach((panel) => {
      panel.hidden = panel !== next;
      if (panel.hidden) panel.querySelectorAll('video').forEach(video => video.pause());
    });
    this.activeId = String(id);
    this.thumbs.forEach((thumb) => {
      const active = thumb.dataset.mediaTarget === this.activeId;
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
      thumb.tabIndex = active ? 0 : -1;
    });
    const counter = this.querySelector('[data-media-current]');
    if (counter) counter.textContent = index + 1;
    this.querySelectorAll('[data-gallery-step]').forEach(button => {
      button.disabled = Number(button.dataset.galleryStep) < 0 ? index === 0 : index === this.panels.length - 1;
    });
    const status = this.querySelector('[data-gallery-status]');
    if (announce && status) status.textContent = status.dataset.mediaStatus.replace('[current]', index + 1);
    this.positionNavigation();
    this.centerThumbnail(animate);
    if (animate && !this.motion.matches && previous !== -1 && next.animate) {
      this.transition = next.animate([{opacity: .45, transform: `translateX(${index > previous ? 10 : -10}px)`}, {opacity: 1, transform: 'translateX(0)'}], {duration: 200, easing: 'cubic-bezier(.2,.7,.3,1)'});
    }
  }
}

class ProductDetail extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.events = new AbortController();
    this.addEventListener('click', (event) => this.onClick(event), {signal: this.events.signal});
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-selling-plan]')) {
        const url = new URL(location.href);
        if (event.target.value) url.searchParams.set('selling_plan', event.target.value);
        else url.searchParams.delete('selling_plan');
        this.updateVariant(url, event.target.id || null);
      }
    }, {signal: this.events.signal});
    this.addEventListener('submit', (event) => {
      if (event.target.matches('.product-form')) this.addToCart(event);
    }, {signal: this.events.signal});
    this.observePurchase();
  }
  disconnectedCallback() {
    this.events?.abort();
    this.variantRequest?.abort();
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    document.documentElement.style.removeProperty('--purchase-offset');
  }
  observePurchase() {
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    const row = this.querySelector('[data-purchase-row]');
    const sticky = this.querySelector('.sticky-purchase');
    if (!row || !sticky || !('IntersectionObserver' in window)) return;
    const updateOffset = () => {
      document.documentElement.style.setProperty('--purchase-offset', sticky.hidden ? '0px' : `${sticky.getBoundingClientRect().height}px`);
    };
    this.observer = new IntersectionObserver(([entry]) => {
      sticky.hidden = entry.isIntersecting || entry.boundingClientRect.bottom > 0;
      updateOffset();
    });
    this.observer.observe(row);
    this.resizeObserver = new ResizeObserver(updateOffset);
    this.resizeObserver.observe(sticky);
  }
  onClick(event) {
    const option = event.target.closest('[data-option-link]');
    if (option && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
      const url = new URL(option.href);
      // Combined-listing siblings use a full native navigation, updating all sections and SEO.
      if (url.pathname !== new URL(this.dataset.productUrl, location.origin).pathname) return;
      event.preventDefault();
      if (!this.adding) this.updateVariant(url, option.id);
    }
    const step = event.target.closest('[data-quantity]');
    if (step) {
      const input = this.querySelector('[name="quantity"]');
      if (Number(step.dataset.quantity) > 0) input.stepUp(); else input.stepDown();
      input.dispatchEvent(new Event('change', {bubbles: true}));
    }
    if (event.target.closest('[data-sticky-add]')) this.querySelector('.product-form').requestSubmit(this.querySelector('[data-add-button]'));
  }
  setBusy(busy) {
    const form = this.querySelector('.product-form');
    form?.setAttribute('aria-busy', String(busy));
    this.querySelectorAll('[data-add-button], [data-sticky-add], [data-selling-plan]').forEach((button) => {
      if (busy) { if (!button.hasAttribute('data-was-disabled')) button.dataset.wasDisabled = String(button.disabled); button.disabled = true; }
      else if (button.hasAttribute('data-was-disabled')) { button.disabled = button.dataset.wasDisabled === 'true'; delete button.dataset.wasDisabled; }
    });
  }
  showError(message) {
    const error = this.querySelector('.product-error');
    error.textContent = message;
    error.hidden = false;
    this.querySelector('.cart-recovery').hidden = false;
    error.focus({preventScroll: true});
  }
  async updateVariant(url, focusId) {
    this.variantRequest?.abort();
    const controller = new AbortController();
    this.variantRequest = controller;
    const timeout = setTimeout(() => controller.abort('timeout'), 15000);
    const requestURL = new URL(url);
    requestURL.searchParams.set('section_id', this.dataset.sectionId);
    this.querySelector('[data-product-info]').setAttribute('aria-busy', 'true');
    this.setBusy(true);
    try {
      const response = await fetch(requestURL, {signal: controller.signal, headers: {'Accept': 'text/html'}});
      if (!response.ok) throw new Error('section');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector('product-detail');
      if (!fresh || !fresh.querySelector('.product-form')) throw new Error('section');
      if (this.variantRequest !== controller || !this.isConnected) return;
      const oldQuantity = Number(this.querySelector('[name="quantity"]').value);
      // Preserve app blocks and their state. Replace only the native variant-dependent markup.
      for (const selector of ['.product-price', '.tax-note', '.variant-picker', '.product-form', '.sticky-purchase']) {
        const oldNode = this.querySelector(selector);
        const newNode = fresh.querySelector(selector);
        if (oldNode && newNode) oldNode.replaceWith(newNode);
      }
      const quantity = this.querySelector('[name="quantity"]');
      const min = Number(quantity.min) || 1;
      const max = quantity.max ? Number(quantity.max) : Infinity;
      const step = Number(quantity.step) || 1;
      quantity.value = Math.max(min, Math.min(max, min + Math.floor((oldQuantity - min) / step) * step));
      this.dataset.variantId = fresh.dataset.variantId;
      const activeMedia = fresh.querySelector('.gallery-panel:not([hidden])')?.dataset.mediaId;
      if (activeMedia) this.querySelector('product-gallery')?.showMedia(activeMedia);
      url.searchParams.delete('section_id');
      url.searchParams.delete('variant');
      if (this.dataset.variantId) { url.searchParams.set('variant', this.dataset.variantId); url.searchParams.delete('option_values'); }
      history.replaceState({}, '', url);
      if (focusId) document.getElementById(focusId)?.focus({preventScroll: true});
      window.Shopify?.PaymentButton?.init();
      this.observePurchase();
      this.dispatchEvent(new CustomEvent('product:variant-change', {bubbles: true, detail: {variantId: this.dataset.variantId}}));
    } catch (error) {
      if (this.variantRequest === controller && (error.name !== 'AbortError' || controller.signal.reason === 'timeout')) {
        this.querySelectorAll('[data-selling-plan]').forEach((select) => {
          const original = [...select.options].find((option) => option.defaultSelected) || select.options[0];
          if (original) select.value = original.value;
        });
        this.showError(this.dataset.variantError);
      }
    } finally {
      clearTimeout(timeout);
      if (this.variantRequest === controller) {
        this.querySelector('[data-product-info]')?.removeAttribute('aria-busy');
        this.setBusy(false);
        this.variantRequest = null;
      }
    }
  }
  async addToCart(event) {
    event.preventDefault();
    if (this.adding || this.variantRequest) return;
    const form = event.target;
    if (!form.reportValidity()) return;
    const body = new FormData(form);
    body.set('sections', 'cart-preview');
    body.set('sections_url', location.pathname);
    this.adding = true;
    this.setBusy(true);
    this.querySelector('.product-error').hidden = true;
    this.querySelector('.cart-recovery').hidden = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(`${this.dataset.addUrl}.js`, {method: 'POST', body, signal: controller.signal, headers: {'Accept': 'application/json'}});
      const result = await response.json();
      if (!response.ok || result.status) {
        this.showError(typeof result.description === 'string' ? result.description : this.dataset.errorMessage);
        return;
      }
      const markup = result.sections?.['cart-preview'];
      if (!markup) { location.assign(this.dataset.cartUrl); return; }
      const panel = document.querySelector('cart-panel');
      if (!panel?.render) { location.assign(this.dataset.cartUrl); return; }
      if (!panel.render(markup, {open: true, trigger: event.submitter || document.activeElement})) {
        location.assign(this.dataset.cartUrl); return;
      }
      document.getElementById('SiteStatus').textContent = this.dataset.addedMessage;
      this.dispatchEvent(new CustomEvent('cart:updated', {bubbles: true}));
    } catch {
      // A failed response can follow a successful POST. Do not automatically re-submit.
      this.showError(this.dataset.errorMessage);
    } finally {
      clearTimeout(timeout);
      this.adding = false;
      this.setBusy(false);
    }
  }
}
if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);
if (!customElements.get('product-detail')) customElements.define('product-detail', ProductDetail);
